import { needsInitialPasswordSetupRoute } from '@/features/auth/lib/initial-password-setup'
import { ApiError, type ApiResponse } from './types'
import { logger } from '@/lib/logger'

type AxiosLikeError = {
  response?: { status?: number; data?: ApiResponse }
  config?: {
    url?: string
    method?: string
    skipUnauthorizedRedirect?: boolean
  }
  message?: string
}

export function envelopeDataRecord(data: unknown): Record<string, unknown> | null {
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return data as Record<string, unknown>
  }
  return null
}

function unauthorizedRedirectPath(): string {
  return needsInitialPasswordSetupRoute() ? '/set-password' : '/login'
}

export function redirectIfUnauthorized(
  isUnauthorized: boolean,
  skipRedirect: boolean
): void {
  if (!isUnauthorized || skipRedirect || globalThis.window === undefined) {
    return
  }
  globalThis.window.location.href = unauthorizedRedirectPath()
}

function requestMeta(error: AxiosLikeError) {
  return {
    status: error.response?.status ?? 0,
    url: error.config?.url ?? 'unknown',
    method: error.config?.method?.toUpperCase() ?? 'UNKNOWN',
    skipRedirect: error.config?.skipUnauthorizedRedirect === true,
  }
}

function buildApiError(body: ApiResponse, status: number): ApiError {
  return new ApiError(
    body.message,
    status,
    body.error!.code,
    body.error!.details,
    envelopeDataRecord(body.data)
  )
}

function rejectStructuredBodyError(error: AxiosLikeError): Promise<never> {
  const body = error.response?.data
  if (!body?.error) {
    return rejectNetworkError(error)
  }

  const { status, url, method, skipRedirect } = requestMeta(error)
  const apiError = buildApiError(body, status)

  logger.error('API request failed', {
    err: apiError,
    status,
    code: body.error.code,
    method,
    url,
  })

  redirectIfUnauthorized(apiError.isUnauthorized, skipRedirect)
  return Promise.reject(apiError)
}

function rejectNetworkError(error: AxiosLikeError): Promise<never> {
  const { status, url, method, skipRedirect } = requestMeta(error)
  const body = error.response?.data

  redirectIfUnauthorized(status === 401, skipRedirect)

  const networkError = new ApiError(
    body?.message ?? error.message ?? 'Network error',
    status,
    body?.error?.code ?? 'network_error',
    null
  )

  logger.error('Network / unexpected API error', {
    err: error,
    status,
    method,
    url,
  })

  return Promise.reject(networkError)
}

export function handleAxiosResponseError(error: unknown): Promise<never> {
  if (error instanceof ApiError) {
    logger.error('API error', {
      err: error,
      status: error.status,
      code: error.code,
    })
    return Promise.reject(error)
  }

  const axiosError = error as AxiosLikeError
  if (axiosError.response?.data?.error) {
    return rejectStructuredBodyError(axiosError)
  }

  return rejectNetworkError(axiosError)
}
